import { formatLocalHm } from '@shared/clock';

export { formatClockInText, formatLocalHm } from '@shared/clock';

export function formatDriveTime(minutes: number): string {
  if (minutes < 2) return 'a short walk';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Statute mile. Display only — haversine and drive-time stay in kilometers. */
const METERS_PER_MILE = 1609.344;
const FEET_PER_MILE = 5280;

/**
 * Imperial label for a kilometer value.
 * Under a tenth of a mile, nearest 10 feet (`500 ft`). Otherwise miles (`0.7 mi`).
 */
export function formatDistance(km: number): string {
  if (!Number.isFinite(km) || km <= 0) return '0 ft';
  const miles = (km * 1000) / METERS_PER_MILE;
  if (miles < 0.1) {
    const feet = Math.round((miles * FEET_PER_MILE) / 10) * 10;
    return `${feet} ft`;
  }
  if (miles < 10) {
    const tenths = (Math.round(miles * 10) / 10).toFixed(1).replace(/\.0$/, '');
    return `${tenths} mi`;
  }
  return `${Math.round(miles)} mi`;
}

/** Imperial label for a km/h value. The stored wind speed stays km/h. */
export function formatSpeedMph(kmh: number): string {
  if (!Number.isFinite(kmh) || kmh <= 0) return '0 mph';
  return `${Math.round((kmh * 1000) / METERS_PER_MILE)} mph`;
}

export function formatDateLabel(dateKey: string): string {
  return new Date(`${dateKey}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Zone suffix at the end of an ISO timestamp. A `Z` earlier in the string does not count. */
const ZONED_ISO = /(?:[zZ]|[+-]\d{2}:\d{2})$/;

/** Wall clock in Portugal. Sun times must not follow the phone's timezone. */
function lisbonHm(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Lisbon',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  let hour = parts.find((part) => part.type === 'hour')?.value ?? '';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
  if (hour === '24') hour = '00';
  if (!/^\d{2}$/.test(hour) || !/^\d{2}$/.test(minute)) return '';
  return `${hour}:${minute}`;
}

/**
 * 12-hour clock from an ISO time.
 * A Lisbon local time with no zone (`2026-10-22T18:47`) stays 6:47 PM.
 * A zoned instant is shown in Europe/Lisbon, never the device clock.
 */
export function clockFromIso(iso: string): string {
  if (!iso) return '';
  if (ZONED_ISO.test(iso)) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const hm = lisbonHm(d);
    return hm ? formatLocalHm(hm) : '';
  }
  const match = iso.match(/T(\d{2}:\d{2})/);
  if (match?.[1]) return formatLocalHm(match[1]);
  return formatLocalHm(iso);
}

/**
 * Sunrise and sunset for the cards.
 * Identical values are the offline stamp of "now" on both fields — hide them
 * instead of printing the current clock twice.
 */
export function sunClocks(sunrise: string, sunset: string): { sunrise: string; sunset: string } {
  if (sunrise && sunrise === sunset) return { sunrise: '', sunset: '' };
  return { sunrise: clockFromIso(sunrise), sunset: clockFromIso(sunset) };
}

/** Unicode DEGREE SIGN (U+00B0). Never U+00BA masculine ordinal or a letter o. */
export const DEGREE = '\u00B0';

export function celsiusToFahrenheit(c: number): number {
  return Math.round((c * 9) / 5 + 32);
}

/** Chip / US default: `72°F` — one degree glyph, then F. */
export function formatTempF(celsius: number): string {
  return `${celsiusToFahrenheit(celsius)}${DEGREE}F`;
}

/** Compact strip: `72°` (Fahrenheit). */
export function formatTempDeg(celsius: number): string {
  return `${celsiusToFahrenheit(celsius)}${DEGREE}`;
}
